// Agentic Call Readiness (operator decision 1-A, 2026-09-03): the facts and
// the checklist behind the "Google can call your business for searchers"
// card on the GBP screen. Pure leaf, no imports.
//
// HONESTY RULES (CLAUDE.md #18, brief §2.4):
//   - Everything stated comes from Google's own help page, verified on the
//     date below. A test fails when that date is older than 90 days: the
//     exclusion list is regulatory and moves, so "verified at build time"
//     is enforced, not remembered.
//   - Google publishes NO category eligibility list. We therefore never say
//     "your category is eligible"; we say what Google says it calls about
//     (bookings, price/availability, restaurant wait times) and let the
//     owner read their own situation.
//   - No advice on opting in or out -- the trade-off is stated, the choice
//     is the owner's. Nothing here posts to Google.
//   - Nothing is measured about the phone LINE; the rows are conditions we
//     can read from data we already hold.

export const AGENTIC_CALLS_FACTS = {
  /** Re-verify at https://support.google.com/business/answer/16190256 and bump. */
  verifiedOn: '2026-09-04',
  source: 'https://support.google.com/business/answer/16190256',
  /** "In the US, the feature is available in all states except Indiana,
   *  Louisiana, Minnesota, Montana, and Nebraska." */
  excludedStates: ['IN', 'LA', 'MN', 'MT', 'NE'] as const,
  /** Verbatim reasons from the help page ("Google will only call you when…"). */
  purposes: [
    'a customer wants to book an appointment',
    'a customer wants to check the restaurant wait time',
    'a customer wants to confirm the price and availability of your products or services',
    'it needs to check your business hours',
    'it needs to check the status of in-demand inventory',
  ] as const,
  /** Where the owner turns it off: Business Profile → More → Business Profile
   *  settings → Advanced settings → "Google automated calls and text messages". */
  optOutPath: 'Business Profile → More → Business Profile settings → Advanced settings → Google automated calls and text messages',
  optOutUrl: 'https://business.google.com/',
} as const;

export const AGENTIC_FACTS_MAX_AGE_DAYS = 90;

const STATE_NAMES: Record<string, string> = { IN: 'Indiana', LA: 'Louisiana', MN: 'Minnesota', MT: 'Montana', NE: 'Nebraska' };

/** Full name -> code, so a business-info field typed as "California" still
 *  resolves (the GBP address carries the code; the site record is free text). */
const US_STATE_CODES: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO', connecticut: 'CT',
  delaware: 'DE', 'district of columbia': 'DC', florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID', illinois: 'IL',
  indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE',
  nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC',
  'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT', virginia: 'VA',
  washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
};

/** Days since `verifiedOn` at `now`; the freshness test and the card footer
 *  both read it. */
export function agenticFactsAgeDays(now: Date): number {
  const verified = Date.parse(`${AGENTIC_CALLS_FACTS.verifiedOn}T00:00:00Z`);
  return Math.floor((now.getTime() - verified) / 86_400_000);
}

export interface AgenticReadinessInput {
  /** Two-letter US state from the GBP address, else the site record. */
  region: string | null;
  /** True when the GBP location is a service-area business with no storefront address. */
  serviceArea: boolean;
  /** The profile's phone as Google returned it (null when the profile has none). */
  gbpPhoneRaw: string | null;
  /** Digits of the profile phone, null when absent OR unparseable. */
  gbpPhoneDigits: string | null;
  sitePhoneDigits: string | null;
  /** True when the NAP-divergence card above already reports a phone mismatch. */
  napPhoneDiverges: boolean;
  hoursListed: boolean;
  primaryCategory: string | null;
}

export type AgenticRowStatus = 'ok' | 'attention' | 'info';

export interface AgenticRow {
  key: 'state' | 'phone' | 'hours' | 'purpose';
  status: AgenticRowStatus;
  label: string;
  detail: string;
}

export interface AgenticReadiness {
  /** false only when the business is in an excluded state (the feature does
   *  not reach it); null when we do not know the state. */
  available: boolean | null;
  rows: AgenticRow[];
}

// Restaurant-family words only (not "food", "coffee", "bar": a pet-food store
// or a coffee roaster is not a restaurant with a wait time).
const RESTAURANT_LIKE = /\b(restaurant|diner|bistro|grill|pizzeria|pizza|taqueria|eatery|cafe|caf\u00e9|steakhouse|sushi|brasserie)\b/i;

function normalizeRegion(region: string | null): string | null {
  if (!region) return null;
  const r = region.trim();
  if (/^[A-Za-z]{2}$/.test(r)) return r.toUpperCase();
  return US_STATE_CODES[r.toLowerCase()] ?? null;
}

export function assessAgenticReadiness(input: AgenticReadinessInput): AgenticReadiness {
  const rows: AgenticRow[] = [];
  const region = normalizeRegion(input.region);
  let available: boolean | null = null;

  const whereFrom = input.serviceArea ? ' (read from your business info, since your profile has no storefront address)' : '';
  if (region === null) {
    rows.push({ key: 'state', status: 'info', label: 'Your state', detail: 'We could not read a US state from your profile or business info, so we cannot say whether the feature reaches you.' });
  } else if ((AGENTIC_CALLS_FACTS.excludedStates as readonly string[]).includes(region)) {
    available = false;
    rows.push({ key: 'state', status: 'info', label: 'Your state', detail: `${STATE_NAMES[region] ?? region}${whereFrom}: Google says the feature is not available here, so it will not call your business.` });
  } else {
    available = true;
    rows.push({ key: 'state', status: 'ok', label: 'Your state', detail: `${region}${whereFrom}: the feature is available (Google excludes only ${Object.values(STATE_NAMES).join(', ')}).` });
  }

  if (!input.gbpPhoneRaw) {
    rows.push({ key: 'phone', status: 'attention', label: 'Phone on your profile', detail: 'Your Google profile has no phone number. Google calls the number on the profile; with none, it cannot call you at all.' });
  } else if (!input.gbpPhoneDigits) {
    rows.push({ key: 'phone', status: 'info', label: 'Phone on your profile', detail: `Google would call ${input.gbpPhoneRaw}. We could not read it as a US number to compare it with your business info.` });
  } else if (input.napPhoneDiverges) {
    rows.push({ key: 'phone', status: 'info', label: 'Phone on your profile', detail: 'Google will call the number on your profile. It differs from your business info; see the mismatch above.' });
  } else if (input.sitePhoneDigits && input.sitePhoneDigits !== input.gbpPhoneDigits) {
    rows.push({ key: 'phone', status: 'attention', label: 'Phone on your profile', detail: 'The phone on your Google profile differs from the one in your business info. Google will call the profile number.' });
  } else if (!input.sitePhoneDigits) {
    rows.push({ key: 'phone', status: 'ok', label: 'Phone on your profile', detail: `Google would call ${input.gbpPhoneRaw}. Your business info has no phone to compare it with.` });
  } else {
    rows.push({ key: 'phone', status: 'ok', label: 'Phone on your profile', detail: `Google would call ${input.gbpPhoneRaw}, and it matches your business info.` });
  }

  rows.push(
    input.hoursListed
      ? { key: 'hours', status: 'ok', label: 'Opening hours', detail: 'Hours are listed on your profile.' }
      : { key: 'hours', status: 'attention', label: 'Opening hours', detail: 'No hours are listed. Google says it also calls to check business hours.' },
  );

  const restaurantLike = !!input.primaryCategory && RESTAURANT_LIKE.test(input.primaryCategory);
  rows.push({
    key: 'purpose',
    status: 'info',
    label: 'What Google asks',
    detail: restaurantLike
      ? 'Google says it calls when a customer wants to book, to check a restaurant’s wait time, to confirm price and availability, or to check hours or in-demand inventory.'
      : 'Google says it calls when a customer wants to book or to confirm the price and availability of what you offer, or when it needs to check your hours or in-demand inventory. Google does not publish a list of eligible business categories.',
  });

  return { available, rows };
}
